/**
 * Dashboard — Configural landing page after login.
 *
 *   - Welcome hero with the logged-in user's name + current org
 *   - Stat tiles for the current org's library + tenants
 *   - Recent library activity (last ~6 changes across prompts, protocols,
 *     agents, tools) inferred from each list's newest created_at
 *   - Quick links to the main authoring surfaces
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  StatCard,
} from "../components.jsx";
import { useAuth } from "../AuthContext.jsx";

export default function Dashboard() {
  const { user, currentOrg, inOrgContext, isPlatformAdmin } = useAuth();

  if (!inOrgContext) {
    return <PlatformDashboard user={user} isPlatformAdmin={isPlatformAdmin} />;
  }
  return <OrgDashboard user={user} currentOrg={currentOrg} isPlatformAdmin={isPlatformAdmin} />;
}

// ─── Platform dashboard (platform admin, no org selected) ───────────
function PlatformDashboard({ user, isPlatformAdmin }) {
  const [orgs, setOrgs] = useState([]);
  const [userCount, setUserCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [orgsRes, usersRes] = await Promise.all([
          api.listOrganizations().catch(() => ({ data: [] })),
          api.listAppUsers().catch(() => ({ data: [] })),
        ]);
        setOrgs(orgsRes.data || []);
        setUserCount((usersRes.data || []).length);
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const firstName = (user?.name || user?.email || "").split(" ")[0] || "there";
  const timeOfDay = (() => {
    const h = new Date().getHours();
    if (h < 12) return "morning";
    if (h < 18) return "afternoon";
    return "evening";
  })();

  return (
    <>
      <PageHeader
        title={<>Good {timeOfDay}, {firstName}</>}
        subtitle={
          isPlatformAdmin
            ? "Platform admin view. Pick an organization below to enter its workspace — or manage the full fleet from the Organizations page."
            : "You don't have an active organization."
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="stat-grid">
              <StatCard
                label="Organizations"
                value={orgs.length}
                sub={orgs.length === 1 ? "1 workspace" : `${orgs.length} workspaces`}
              />
              <StatCard
                label="Users"
                value={userCount ?? "—"}
                sub="Across every organization"
              />
              <StatCard
                label="Your role"
                value="Platform admin"
                sub="Can see and edit every org"
              />
            </div>

            <div className="card">
              <div className="card-header">
                <h3>Organizations</h3>
                <Link to="/admin/organizations" className="btn sm">
                  Manage all
                </Link>
              </div>
              {orgs.length === 0 ? (
                <div className="muted small" style={{ padding: "16px 0" }}>
                  No organizations yet. Head to{" "}
                  <Link to="/admin/organizations">Organizations</Link> and
                  create the first one.
                </div>
              ) : (
                <div className="org-grid">
                  {orgs.map((o) => (
                    <div key={o._id} className="org-card">
                      <div className="org-card-top">
                        <div
                          className="org-card-avatar"
                          style={{ background: o.brand_color || "#0f0f0f" }}
                        >
                          {(o.name || o._id).charAt(0).toUpperCase()}
                        </div>
                        <div className="org-card-heading">
                          <div className="org-card-name">{o.name}</div>
                          <div className="org-card-slug mono">{o._id}</div>
                        </div>
                      </div>
                      <p className="org-card-description">
                        {o.description || (
                          <span className="subtle">No description</span>
                        )}
                      </p>
                      <div className="org-card-actions">
                        <OpenOrgButton slug={o._id} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function OpenOrgButton({ slug }) {
  const { enterOrg } = useAuth();
  return (
    <Link
      to="/"
      className="btn sm primary"
      style={{ width: "100%", justifyContent: "center" }}
      onClick={() => enterOrg(slug)}
    >
      Open workspace →
    </Link>
  );
}

// ─── Org dashboard (any user with an active org) ───────────────────
function OrgDashboard({ user, currentOrg, isPlatformAdmin }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({
    tenants: 0,
    agents: 0,
    tools: 0,
    protocols: 0,
    specialties: 0,
  });
  const [release, setRelease] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [tenants, agents, tools, protocols, catalog, releaseRes] =
          await Promise.all([
            api.listTenants().catch(() => ({ data: [] })),
            api.listAgents({ mode: "live" }).catch(() => ({ data: [] })),
            api.listTools().catch(() => ({ data: [] })),
            api.listProtocols({ mode: "live" }).catch(() => ({ data: [] })),
            api.listSpecialtyCatalog().catch(() => ({ data: [] })),
            api.getCurrentLibraryRelease().catch(() => ({ data: null })),
          ]);

        setCounts({
          tenants: (tenants.data || []).length,
          agents: (agents.data || []).length,
          tools: (tools.data || []).length,
          protocols: (protocols.data || []).length,
          specialties: (catalog.data || []).length,
        });
        setRelease(releaseRes.data || null);

        // Build a "recent activity" feed from each doc's created_at
        const items = [];
        for (const a of agents.data || []) {
          items.push({
            kind: "agent",
            label: a.name || a.key,
            detail: (a.specialties || []).join(", ") || "—",
            at: a.created_at,
            to: `/library/draft/agents/${encodeURIComponent(a.key)}`,
          });
        }
        for (const p of protocols.data || []) {
          items.push({
            kind: "protocol",
            label: p.name || p.protocol_id,
            detail: p.category,
            at: p.created_at,
            to: `/library/draft/protocols/${encodeURIComponent(
              p.category
            )}/${encodeURIComponent(p.protocol_id)}`,
          });
        }
        for (const t of tools.data || []) {
          items.push({
            kind: "tool",
            label: t.name || t.tool_id || t._id,
            detail: t.kind || "tool",
            at: t.updated_at || t.created_at,
            // Link must use tool_id (the slug key), not the Mongo _id —
            // getTool looks up by tool_id.
            to: `/library/draft/tools/${encodeURIComponent(t.tool_id || t._id)}`,
          });
        }
        items.sort((a, b) =>
          new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime()
        );
        setRecent(items.slice(0, 6));
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentOrg?._id]);

  const firstName = (user?.name || user?.email || "").split(" ")[0] || "there";
  const timeOfDay = (() => {
    const h = new Date().getHours();
    if (h < 12) return "morning";
    if (h < 18) return "afternoon";
    return "evening";
  })();

  return (
    <>
      <PageHeader
        title={<>Good {timeOfDay}, {firstName}</>}
        subtitle={
          currentOrg
            ? `You're viewing ${currentOrg.name}. Jump back into the library or browse recent changes below.`
            : "Pick an organization from the switcher above to get started."
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        {loading ? (
          <Loading />
        ) : (
          <>
            {/* Current org hero card */}
            {currentOrg && (
              <div className="dashboard-hero">
                <div
                  className="dashboard-hero-avatar"
                  style={{ background: currentOrg.brand_color || "#0f0f0f" }}
                >
                  {(currentOrg.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="dashboard-hero-meta">
                  <div className="dashboard-hero-label">Current organization</div>
                  <div className="dashboard-hero-name">
                    {currentOrg.name}
                    <span className="mono small muted" style={{ marginLeft: 10 }}>
                      {currentOrg._id}
                    </span>
                  </div>
                  {currentOrg.description && (
                    <p className="dashboard-hero-description">
                      {currentOrg.description}
                    </p>
                  )}
                </div>
                {release && (
                  <div className="dashboard-hero-release">
                    <div className="dashboard-hero-release-label">
                      Current prod release
                    </div>
                    <div className="dashboard-hero-release-version mono">
                      v{release.version}
                    </div>
                    <Link
                      to="/library/live/versions"
                      className="dashboard-hero-release-link"
                    >
                      View history →
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Library stat tiles */}
            <div className="stat-grid">
              <StatCard
                label="Agents"
                value={counts.agents}
                sub="In the live library"
              />
              <StatCard
                label="Tools"
                value={counts.tools}
                sub="Server + client + builtin"
              />
              <StatCard
                label="Agent Rules"
                value={counts.protocols}
                sub="Across every category"
              />
              <StatCard
                label="Agent Groups"
                value={counts.specialties}
                sub="Tagged groupings"
              />
              <StatCard
                label="Tenants"
                value={counts.tenants}
                sub="Clinical customers"
              />
            </div>

            {/* Two-column layout: recent activity + quick actions */}
            <div className="dashboard-columns">
              <div className="card dashboard-activity">
                <div className="card-header">
                  <h3>Recent library activity</h3>
                  <Link to="/library/draft/revisions" className="btn sm">
                    View revisions
                  </Link>
                </div>
                {recent.length === 0 ? (
                  <div className="muted small" style={{ padding: "16px 0" }}>
                    No activity in this workspace yet.
                  </div>
                ) : (
                  <ul className="dashboard-activity-list">
                    {recent.map((r, i) => (
                      <li key={i}>
                        <span className={`activity-dot activity-dot-${r.kind}`} />
                        <div className="activity-body">
                          <div className="activity-heading">
                            <Link to={r.to}>{r.label}</Link>
                            <span className="muted small">
                              {" · "}
                              {r.kind}
                            </span>
                          </div>
                          {r.detail && (
                            <div className="muted small">{r.detail}</div>
                          )}
                        </div>
                        <div className="muted small">
                          {r.at
                            ? new Date(r.at).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                              })
                            : "—"}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="card dashboard-quick">
                <div className="card-header">
                  <h3>Quick actions</h3>
                </div>
                <div className="dashboard-quick-list">
                  <QuickLink
                    to="/library/draft/agents"
                    icon="◎"
                    title="Manage agents"
                    description="Compose and version your agent library."
                  />
                  <QuickLink
                    to="/library/draft/tools"
                    icon="▽"
                    title="Tools registry"
                    description="Server, client, and built-in tool definitions."
                  />
                  <QuickLink
                    to="/library/draft/protocols"
                    icon="▤"
                    title="Agent Rules"
                    description="Reusable rule blocks agents load at runtime via tool calls."
                  />
                  <QuickLink
                    to="/library/draft/revisions"
                    icon="↻"
                    title="Revisions"
                    description="Pending drafts + push to prod."
                  />
                  <QuickLink
                    to="/users"
                    icon="▣"
                    title="Users"
                    description="Invite and manage your team."
                  />
                  {isPlatformAdmin && (
                    <QuickLink
                      to="/admin/organizations"
                      icon="◉"
                      title="Organizations"
                      description="Platform admin — every workspace on Configural."
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function QuickLink({ to, icon, title, description }) {
  return (
    <Link to={to} className="quick-link">
      <div className="quick-link-icon">{icon}</div>
      <div className="quick-link-body">
        <div className="quick-link-title">{title}</div>
        <div className="quick-link-desc">{description}</div>
      </div>
      <span className="quick-link-arrow">→</span>
    </Link>
  );
}
